(() => {
  // --- Discover API base from the script tag (prefer data-api) ---
  const SCRIPT_EL =
    document.currentScript ||
    [...document.getElementsByTagName('script')]
      .reverse()
      .find((s) => s.src && /(?:^|\/)wishlist\.js(?:\?|$)/.test(s.src));

  const API_BASE = SCRIPT_EL?.dataset?.api?.replace(/\/$/, '') || '/apps/wishlist';

  // Accept:
  // data-debug="1" / "true" / "yes" / "on"
  const DEBUG_RAW = SCRIPT_EL?.dataset?.debug ?? '0';
  const DEBUG = /^(1|true|yes|on)$/i.test(String(DEBUG_RAW));

  // --- Logger (all log() calls are DEBUG-gated here) ---
  const log = (...args) => {
    if (!DEBUG) return;
    console.log('[Wishlist]', ...args);
  };

  // --- Core fetch helper with timeout + robust error reporting ---
  async function api(path, opts = {}) {
    const url = `${API_BASE}${path}`;
    const method = opts.method || 'GET';
    const timeout = opts.timeout ?? 60_000;

    const controller = new AbortController();
    const timer = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

    let headers = {
      Accept: 'application/json',
      ...(opts.headers || {}),
    };

    let body = null;
    if (opts.body != null) {
      if (opts.bodyType === 'form') {
        body = new URLSearchParams(opts.body);
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      } else {
        body = JSON.stringify(opts.body);
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      }
    }

    log('➡️ Fetching:', url, 'Options:', {
      method,
      timeout,
      headers,
      bodyType: opts.bodyType,
    });

    let res;
    try {
      res = await fetch(url, {
        method,
        headers,
        body,
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        const e = Object.assign(
          new Error(`Request timed out after ${timeout} ms`),
          { code: 'TIMEOUT' }
        );
        log('⏱️ Timeout:', e.message);
        throw e;
      }

      log('🔌 Network error:', err);
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }

    const reqId =
      res.headers.get('x-request-id') ||
      res.headers.get('x-shopify-request-id') ||
      null;

    const headersObj = {};
    res.headers.forEach((value, key) => {
      headersObj[key] = value;
    });

    const raw = await res.text();
    const contentType = (res.headers.get('content-type') || '').toLowerCase();

    log('⬅️ Status:', res.status, 'OK?', res.ok);
    if (reqId) log('🧾 Request ID:', reqId);
    log('⬅️ Headers:', headersObj);
    log('⬅️ Body (raw):', raw ? raw.slice(0, 400) : '(empty)');

    let json = null;

    if (raw) {
      try {
        json = JSON.parse(raw);
        log('⬅️ Parsed JSON:', json);
      } catch (err) {
        log('⚠️ Response is not valid JSON', {
          contentType,
          preview: raw.slice(0, 200),
        });
      }
    }

    // HTTP failure
    if (!res.ok) {
      const msg =
        json?.error ||
        json?.message ||
        `HTTP ${res.status}`;

      const err = Object.assign(new Error(msg), {
        code: 'HTTP_ERROR',
        status: res.status,
        payload: json,
        raw,
        requestId: reqId || undefined,
      });

      log('❌ API error:', msg);
      throw err;
    }

    // App-level failure from Lambda/Shopify proxy
    if (json?.ok === false) {
      const msg = json.error || json.message || 'Application error';

      const err = Object.assign(new Error(msg), {
        code: 'APP_ERROR',
        status: res.status,
        payload: json,
        requestId: reqId || undefined,
      });

      log('❌ API error:', msg);
      throw err;
    }

    // Protect against Shopify proxy returning HTML with 200
    if (!json) {
      const err = Object.assign(
        new Error('Expected JSON response but received non-JSON content'),
        {
          code: 'INVALID_RESPONSE',
          status: res.status,
          raw,
          requestId: reqId || undefined,
        }
      );

      log('❌ Invalid response:', {
        status: res.status,
        contentType,
        preview: raw.slice(0, 200),
      });
      throw err;
    }

    return json;
  }

  // --- Public API: use FORM posts for app proxy routes by default ---
  async function wishlistAdd(productHandle) {
    log('🟢 Adding to wishlist:', productHandle);
    return api('/add', {
      method: 'POST',
      bodyType: 'form',
      body: { productHandle },
    });
  }

  async function wishlistRemove(productHandle) {
    log('🔴 Removing from wishlist:', productHandle);
    return api('/remove', {
      method: 'POST',
      bodyType: 'form',
      body: { productHandle },
    });
  }

  async function wishlistToggle(productHandle, nextState) {
    log('🔀 Toggling', productHandle, '→', nextState ? 'ADD' : 'REMOVE');
    return nextState ? wishlistAdd(productHandle) : wishlistRemove(productHandle);
  }

  // --- UI helpers ---
  const BTN_SEL = '.wishlist-btn';
  const BTN_DEL = '.wishlist-remove-btn';

  function setState(btn, saved) {
    btn.classList.toggle('is-in-wishlist', !!saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      saved ? 'Remove from wishlist' : 'Add to wishlist'
    );

    const label = btn.querySelector('.wishlist-btn__label');
    if (label) {
      label.textContent = saved ? '♥ Saved' : '♡ Add';
    } else {
      btn.textContent = saved ? '♥ Saved' : '♡ Add';
    }

    log('🔘 UI updated →', saved ? 'Saved' : 'Not saved');
  }

  function toast(msg) {
    alert(msg); // Replace with theme toast/snackbar when ready
  }

  // --- Add / Toggle buttons ---
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest(BTN_SEL);
    if (!btn) return;

    const handle = btn.dataset.handle;
    if (!handle) {
      log('⚠️ Button clicked but no data-handle found');
      return;
    }

    const currentlySaved = btn.classList.contains('is-in-wishlist');
    const nextSaved = !currentlySaved;

    setState(btn, nextSaved);
    btn.disabled = true;

    try {
      await wishlistToggle(handle, nextSaved);
      log('✅ Wishlist updated:', handle);
    } catch (err) {
      setState(btn, currentlySaved);

      const code = err?.code || '';
      const message = err?.message || 'Something went wrong';

      if (code === 'AUTH_REQUIRED' || /not logged in/i.test(message)) {
        log('🚪 Auth required');
        toast('Please log in to use your wishlist.');
      } else {
        log('❌ Wishlist error:', message);
        toast(`Please Try Again`);
      }
    } finally {
      btn.disabled = false;
    }
  });

  // --- Remove buttons (wishlist page/cards) ---
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest(BTN_DEL);
    if (!btn) return;

    const handle = btn.dataset.handle;
    if (!handle) {
      log('⚠️ Remove button clicked but no data-handle found');
      return;
    }

    btn.disabled = true;

    try {
      await wishlistRemove(handle);
      btn.closest('.wishlist-cell')?.remove();
      log('✅ Removed from wishlist:', handle);
    } catch (err) {
      log('❌ Failed to remove:', err);
      toast(`Remove failed: ${err?.message || 'Error'}`);
    } finally {
      btn.disabled = false;
    }
  });

    window.BigAsiaWishlist = {
    add: wishlistAdd,
    remove: wishlistRemove,
    toggle: wishlistToggle,
  };

  log('✨ Wishlist client ready. API_BASE =', API_BASE, 'DEBUG =', DEBUG);
})();